import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtDateTimeIST } from "@/lib/format";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Loader2, MessageCircle, Clock, Unlink, Search, Sparkles, ExternalLink, Copy, RefreshCw } from "lucide-react";
import { Info } from "@/components/Info";
import { Loading } from "@/components/Loading";

export function WhatsAppTab({ recordId, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sugg, setSugg] = useState([]);
  const [suggState, setSuggState] = useState("loading"); // loading | ok | slow
  const [suggPending, setSuggPending] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [draft, setDraft] = useState(null);
  const [drafting, setDrafting] = useState(false);
  const [replacing, setReplacing] = useState(false);

  // Item 1: linked chats + timeline render immediately from cache; suggestions are a separate request.
  const loadBlock = async () => {
    setLoading(true);
    try { setData((await api.get(`/cases/${recordId}/whatsapp`)).data); }
    catch { toast.error("Failed to load WhatsApp"); }
    finally { setLoading(false); }
  };
  const loadSugg = async () => {
    setSuggState("loading");
    try {
      const r = await api.get(`/whatsapp/suggestions`, { params: { case_record_id: recordId }, timeout: 8000 });
      setSugg(r.data.suggestions || []); setSuggPending(!!r.data.pending); setSuggState("ok");
    } catch { setSuggState("slow"); }
  };
  const load = () => { loadBlock(); loadSugg(); onChanged?.(); };
  useEffect(() => { loadBlock(); loadSugg(); /* eslint-disable-next-line */ }, [recordId]);

  const hasGroup = !!data?.has_customer_group;
  const link = async (chat_id, chat_type, replace = false) => {
    try {
      const r = await api.post(`/cases/${recordId}/whatsapp/link`, { chat_id, chat_type, replace });
      toast.success(`${replace ? "Replaced customer group" : "Linked"}${r.data.importing ? " · importing history in the background" : ""}`);
      setQ(""); setResults([]); setReplacing(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Link failed"); }
  };
  const unlink = async (chat_id) => {
    try { await api.delete(`/cases/${recordId}/whatsapp/link/${encodeURIComponent(chat_id)}`); toast.success("Unlinked"); load(); }
    catch { toast.error("Unlink failed"); }
  };
  const setPrefers = async (value) => {
    try { await api.post(`/cases/${recordId}/whatsapp/prefers-1to1`, { value }); toast.success(value ? "WhatsApp buttons open the 1:1 chat" : "WhatsApp buttons open the group"); load(); }
    catch { toast.error("Failed"); }
  };
  const searchTimer = React.useRef(null);
  const searchSeq = React.useRef(0);
  const search = (val) => {
    setQ(val);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (val.length < 2) { setResults([]); return; }
    searchTimer.current = setTimeout(async () => {
      const seq = ++searchSeq.current;
      try {
        const r = await api.get(`/whatsapp/search`, { params: { q: val } });
        if (seq === searchSeq.current) setResults(r.data.chats || []);
      } catch { /* noop */ }
    }, 350);
  };

  const genDraft = async () => {
    setDrafting(true);
    try { setDraft((await api.post(`/cases/${recordId}/whatsapp/draft`)).data.draft); }
    catch (e) { toast.error(e?.response?.data?.detail || "Draft failed"); }
    finally { setDrafting(false); }
  };

  if (loading && !data) return <div className="max-w-3xl"><Loading label="Loading WhatsApp…" onRetry={loadBlock} section="WhatsApp tab" /></div>;

  // Item 12: once a customer group is linked, other groups can only be linked as partner (or replace the customer group).
  const LinkBtns = ({ chat, idPrefix }) => {
    const likely = chat.likely_type || "customer";
    const isDm = String(chat.chat_id).endsWith("@c.us");
    const cls = (primary) => `text-xs rounded-lg px-2.5 py-1.5 font-medium whitespace-nowrap ${primary ? "bg-[#FF4802] text-white" : "border border-[#E2E2E2] text-[#6B7280] hover:border-[#FF4802]"}`;
    return (
      <div className="flex gap-1.5 flex-shrink-0">
        {(!hasGroup || isDm) && <button onClick={() => link(chat.chat_id, "customer")} data-testid={`${idPrefix}-customer-${chat.chat_id}`} className={cls(!isDm && likely === "customer")}>{isDm ? "Link 1:1 (customer prefers 1:1)" : "Link as customer group"}</button>}
        {hasGroup && !isDm && replacing && <button onClick={() => link(chat.chat_id, "customer", true)} data-testid={`${idPrefix}-replace-${chat.chat_id}`} className={cls(true)}>Use as customer group</button>}
        {!isDm && <button onClick={() => link(chat.chat_id, "partner")} data-testid={`${idPrefix}-partner-${chat.chat_id}`} className={cls(hasGroup ? !replacing : likely === "partner")}>Link as partner group</button>}
      </div>
    );
  };
  const w = data?.waiting;
  const openLink = data?.customer_open_link;
  return (
    <div className="max-w-3xl space-y-5" data-testid="whatsapp-tab">
      {data?.unread_since_last_open > 0 && (
        <p className="text-xs text-[#38A2FF] font-medium" data-testid="wa-unread-since-open">{data.unread_since_last_open} unread WhatsApp message{data.unread_since_last_open === 1 ? "" : "s"} since you last opened this case</p>
      )}
      {w && w.direction === "customer_waiting" && (
        <div className="rounded-2xl border border-[#CA1212]/30 bg-[#CA1212]/5 p-3" data-testid="wa-waiting-badge">
          <div className="flex items-center gap-2 text-sm">
            <Clock className="w-4 h-4 text-[#CA1212]" />
            <span className="text-[#CA1212] font-medium" data-testid="wa-waiting-dur">Customer waiting {w.human || `${w.hours}h`}</span>
            {w.last_message && <span className="text-[#6B7280] truncate">· "{w.last_message}"</span>}
            <button onClick={genDraft} disabled={drafting} data-testid="wa-draft-btn" className="ml-auto text-xs bg-[#159A50] text-white rounded-lg px-3 py-1.5 font-medium disabled:opacity-50">{drafting ? "Drafting…" : "Draft reply"}</button>
          </div>
          {draft != null && (
            <div className="mt-2" data-testid="wa-draft-box">
              <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={4} className="w-full text-sm border border-[#E2E2E2] rounded-lg p-2" />
              <div className="flex gap-2 mt-1">
                <button onClick={() => { navigator.clipboard?.writeText(draft); toast.success("Copied"); if (openLink) window.open(openLink, "_blank"); }} data-testid="wa-draft-copy-open" className="text-xs bg-[#FF4802] text-white rounded-lg px-3 py-1.5 font-medium">{openLink ? "Copy & open WhatsApp" : "Copy"}</button>
              </div>
            </div>
          )}
        </div>
      )}
      {w && w.direction === "gone_quiet" && (
        <div className="rounded-2xl border border-[#E2E2E2] bg-[#F7F7F7] p-3 text-sm text-[#6B7280] flex items-center gap-2" data-testid="wa-waiting-badge">
          <Clock className="w-4 h-4" /> Gone quiet · customer's last message {w.human} ago with no reply
          <button onClick={genDraft} disabled={drafting} data-testid="wa-draft-btn" className="ml-auto text-xs border border-[#159A50]/40 text-[#159A50] rounded-lg px-3 py-1.5 font-medium disabled:opacity-50">{drafting ? "Drafting…" : "Draft reply"}</button>
        </div>
      )}
      {w && w.direction === "gone_quiet" && draft != null && (
        <div data-testid="wa-draft-box"><textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={4} className="w-full text-sm border border-[#E2E2E2] rounded-lg p-2" />
          <button onClick={() => { navigator.clipboard?.writeText(draft); toast.success("Copied"); if (openLink) window.open(openLink, "_blank"); }} data-testid="wa-draft-copy-open" className="mt-1 text-xs bg-[#FF4802] text-white rounded-lg px-3 py-1.5 font-medium">{openLink ? "Copy & open WhatsApp" : "Copy"}</button></div>
      )}
      {w && w.direction === "waiting_on_customer" && (
        <div className="rounded-2xl border border-[#E2E2E2] bg-[#F7F7F7] p-3 text-sm text-[#6B7280]" data-testid="wa-waiting-badge">
          Waiting on customer {w.days}d {w.read === true ? "· read" : w.read === false ? "· not read" : ""}
        </div>
      )}

      {(!w || w.direction === "waiting_on_customer" || !w.direction) && (data?.chats || []).length > 0 && (
        <div className="rounded-2xl border border-[#E2E2E2] bg-white p-3 shadow-sm flex items-center gap-3" data-testid="wa-draft-anytime">
          <span className="text-xs text-[#6B7280]">Need to nudge the customer? Draft a reply from the latest messages.</span>
          <button onClick={genDraft} disabled={drafting} data-testid="wa-draft-btn" className="ml-auto text-xs border border-[#159A50]/40 text-[#159A50] rounded-lg px-3 py-1.5 font-medium disabled:opacity-50">{drafting ? "Drafting…" : "Draft reply"}</button>
        </div>
      )}
      {(!w || w.direction === "waiting_on_customer" || !w.direction) && draft != null && (
        <div data-testid="wa-draft-box"><textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={4} className="w-full text-sm border border-[#E2E2E2] rounded-lg p-2" />
          <button onClick={() => { navigator.clipboard?.writeText(draft); toast.success("Copied"); if (openLink) window.open(openLink, "_blank"); }} data-testid="wa-draft-copy-open" className="mt-1 text-xs bg-[#FF4802] text-white rounded-lg px-3 py-1.5 font-medium">{openLink ? "Copy & open WhatsApp" : "Copy"}</button></div>
      )}
      <div className="rounded-2xl border border-[#E2E2E2] bg-white p-5 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xs font-medium text-[#6B7280] uppercase flex items-center gap-1.5" style={{ letterSpacing: "0.06em" }}><MessageCircle className="w-3.5 h-3.5" /> Linked WhatsApp chats <Info k="link_types" /></h3>
          {data?.customer_dm_link && (
            <label className="flex items-center gap-1.5 text-[11px] text-[#6B7280] cursor-pointer" title="Rare: only when the customer prefers direct messages over the group">
              <input type="checkbox" checked={!!data?.prefers_1to1} onChange={(e) => setPrefers(e.target.checked)} data-testid="wa-prefers-1to1" className="accent-[#FF4802]" /> Customer prefers 1:1
            </label>
          )}
        </div>
        {(data?.chats || []).length === 0 ? <p className="text-sm text-[#6B7280]">No chats linked yet.</p> : (
          <div className="space-y-2" data-testid="wa-linked-list">
            {data.chats.map((c) => {
              const isDm = String(c.chat_id).endsWith("@c.us");
              return (
                <div key={c.chat_id} className="flex items-center justify-between border border-[#E2E2E2] rounded-xl px-3 py-2" data-testid={`wa-chat-${c.chat_id}`}>
                  <div className="min-w-0 mr-3">
                    <div className="text-sm text-[#333333] truncate">{c.name || c.chat_id}</div>
                    <div className="text-[11px] text-[#C5C4C4]">{c.last_message_at ? fmtDateTimeIST(c.last_message_at) : "—"}</div>
                  </div>
                  <div className="flex items-center gap-2 whitespace-nowrap flex-shrink-0 text-xs">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${c.chat_type === "partner" ? "bg-[#8B46FF]/10 text-[#8B46FF]" : "bg-[#159A50]/10 text-[#159A50]"}`}>{c.chat_type}{isDm ? " 1:1" : ""}</span>
                    {c.open_link && <a href={c.open_link} target="_blank" rel="noreferrer" data-testid={`wa-open-${c.chat_id}`} className="text-[#159A50] hover:underline text-xs flex items-center gap-1"><ExternalLink className="w-3 h-3" />{isDm ? "Open chat" : "Open group"}</a>}
                    {c.open_link && <button onClick={() => { navigator.clipboard?.writeText(c.open_link); toast.success("Link copied"); }} data-testid={`wa-copylink-${c.chat_id}`} className="text-[#6B7280] hover:underline text-xs flex items-center gap-1"><Copy className="w-3 h-3" />Copy link</button>}
                    {c.chat_type === "customer" && !isDm && (
                      <button onClick={() => setReplacing((v) => !v)} data-testid={`wa-replace-${c.chat_id}`} className={`text-xs hover:underline ${replacing ? "text-[#FF4802] font-medium" : "text-[#6B7280]"}`}>{replacing ? "Cancel replace" : "Replace customer group"}</button>
                    )}
                    <button onClick={() => unlink(c.chat_id)} data-testid={`wa-unlink-${c.chat_id}`} className="text-[#CA1212] hover:underline text-xs flex items-center gap-1"><Unlink className="w-3 h-3" />Unlink</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {replacing && <p className="text-[11px] text-[#FF4802] mt-2" data-testid="wa-replace-hint">Pick the new customer group below — the current one will be unlinked.</p>}
      </div>

      <div className="rounded-2xl border border-[#E2E2E2] bg-white p-5 shadow-sm" data-testid="wa-suggestions">
        <h3 className="text-xs font-medium text-[#6B7280] uppercase mb-3 flex items-center gap-1.5" style={{ letterSpacing: "0.06em" }}><Sparkles className="w-3.5 h-3.5" /> Suggested chats (confirm to link) <Info k="suggested_chats" /></h3>
        {suggState === "loading" && <div className="flex items-center gap-2 text-xs text-[#6B7280]" data-testid="wa-sugg-loading"><Loader2 className="w-3.5 h-3.5 animate-spin text-[#FF4802]" /> Finding matching chats…</div>}
        {suggState === "slow" && (
          <div className="flex items-center gap-3 text-xs text-[#92600a]" data-testid="wa-sugg-slow">Suggestions are still loading, try again in a minute.
            <button onClick={loadSugg} data-testid="wa-sugg-retry" className="flex items-center gap-1 text-[#FF4802] hover:underline font-medium"><RefreshCw className="w-3 h-3" />Retry</button></div>
        )}
        {suggState === "ok" && sugg.length === 0 && <p className="text-xs text-[#6B7280]" data-testid="wa-sugg-empty">{suggPending ? "Suggestions are being prepared (refreshed every 10 minutes). Search below meanwhile." : "No matching chats found. Search below."}</p>}
        {suggState === "ok" && sugg.length > 0 && (
          <div className="space-y-2">
            {sugg.map((s) => (
              <div key={s.chat_id} className="flex items-center justify-between border border-[#E2E2E2] rounded-xl px-3 py-2" data-testid={`wa-sugg-${s.chat_id}`}>
                <div className="min-w-0">
                  <div className="text-sm text-[#333333] truncate">{s.name || s.chat_id} <span className="text-[11px] text-[#C5C4C4]">· {s.type_label}</span></div>
                  <div className="text-[11px] text-[#6B7280]">{(s.reasons || []).join(" · ")}{s.preview?.text ? <span className="text-[#C5C4C4]"> · "{s.preview.text}"</span> : null}</div>
                </div>
                <LinkBtns chat={s} idPrefix="wa-link-sugg" />
              </div>
            ))}
            <button onClick={async () => { try { await api.post("/suggestions/dismiss", { kind: "case_suggest", key: recordId, case_record_id: recordId }); const keep = sugg; setSugg([]); toast.success("Suggestions hidden for this case", { duration: 6000, action: { label: "Undo", onClick: async () => { await api.post("/suggestions/undismiss", { kind: "case_suggest", key: recordId }); setSugg(keep); } } }); } catch { toast.error("Failed"); } }}
              data-testid="wa-sugg-none" className="text-[11px] text-[#6B7280] hover:underline">None of these</button>
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-[#E2E2E2] bg-white p-5 shadow-sm">
        <h3 className="text-xs font-medium text-[#6B7280] uppercase mb-3 flex items-center gap-1.5" style={{ letterSpacing: "0.06em" }}><Search className="w-3.5 h-3.5" /> Link a chat</h3>
        <Input value={q} onChange={(e) => search(e.target.value)} placeholder="Search your chats by name…" data-testid="wa-search-input" className="border-[#E2E2E2] rounded-lg mb-2" />
        <div className="space-y-2">
          {results.map((c) => (
            <div key={c.chat_id} className="flex items-center justify-between border border-[#E2E2E2] rounded-xl px-3 py-2" data-testid={`wa-result-${c.chat_id}`}>
              <div className="text-sm text-[#333333] truncate">{c.name || c.chat_id} <span className="text-[11px] text-[#C5C4C4]">({c.classification})</span></div>
              <LinkBtns chat={c} idPrefix="wa-link-search" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
